import { useQuery } from '@tanstack/react-query';
import { useCartStore } from '@/stores/cart.store';
import { useAuthStore } from '@/stores/auth.store';
import api from '@/lib/api';
import { usePreviewSession } from '@/hooks/usePreviewSession';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { useOfflinePricingData } from '@/hooks/useOfflinePricingData';
import { previewOfflinePromotions } from '@/lib/offline-pricing';
import { promotionIsInWindow, type EnginePromotion } from '@shared/pricing/promotion-engine';

export interface ActivePromotion {
  id: string;
  name: string;
  type: string;
  discountValue: string;
  minOrderValue: string | null;
  /** Day / time window the engine checks (JSON array of weekdays 0-6, "HH:mm" bounds). */
  daysOfWeek?: string | null;
  timeOfDayStart?: string | null;
  timeOfDayEnd?: string | null;
}

/**
 * POS-I124: keeps only the promotions that can apply right now, using the engine's own day and
 * time window (`promotionIsInWindow`), so a Saturday / Sunday promotion is not advertised on a
 * Monday. Exported for the unit test.
 */
export function promotionsApplyingNow<T extends ActivePromotion>(promotions: T[], now: Date = new Date()): T[] {
  return promotions.filter((p) =>
    promotionIsInWindow(
      {
        daysOfWeek: p.daysOfWeek ?? null,
        timeOfDayStart: p.timeOfDayStart ?? null,
        timeOfDayEnd: p.timeOfDayEnd ?? null,
      } as EnginePromotion,
      now,
    ),
  );
}

export interface PromotionPreviewLine {
  promotionId: string;
  promotionName: string;
  discountAmount: number;
}

/**
 * Single client-side owner for the auto-applied promotion discount on the current
 * cart. (T-356)
 *
 * Backed by `POST /promotions/preview`, which runs the SAME promotion engine
 * (`PromotionsService.findApplicableAndCalculate`) the committed sale uses in
 * `SaleService.createSale` — so the previewed discount equals the discount the
 * sale will actually post. CartPanel and CheckoutDialog both consume this hook
 * instead of each fetching/deriving the discount, so the pre-payment Total they
 * show is computed from one owner and matches the charged total.
 */
const EMPTY_PREVIEW: PromotionPreviewLine[] = [];

export function usePromotionPreview(couponCode?: string) {
  const { hasPermission } = useAuthStore();
  // The informational "active promotions" badge list is a management-read view
  // (`/promotions/active` → promotions:read). Only users with that permission see it.
  const canReadPromotions = hasPermission('promotions:*') || hasPermission('promotions:read');
  const isOnline = useOnlineStatus();

  const items = useCartStore((s) => s.items);
  const subtotal = useCartStore((s) => s.subtotal);
  const orderDiscountAmount = useCartStore((s) => s.orderDiscountAmount);

  // Active promotions — shown as informational badges in the cart.
  const { data: allActivePromotions = [] } = useQuery({
    queryKey: ['active-promotions-pos'],
    queryFn: () =>
      api.get('/promotions/active').then((r) => {
        const d = r.data;
        return (Array.isArray(d) ? d : d?.data ?? []) as ActivePromotion[];
      }),
    staleTime: 60_000,
    enabled: canReadPromotions && isOnline,
  });

  const activePromotions = promotionsApplyingNow(allActivePromotions);

  // Live promotion preview — recalculates as the cart changes.
  const cartPayload =
    items.length > 0
      ? {
          items: items.map((i) => ({
            productId: i.product.id,
            categoryId: i.product.category?.id ?? null,
            quantity: i.quantity,
            lineSubtotal: i.unitPrice * i.quantity - i.discountAmount,
          })),
          orderSubtotal: subtotal - orderDiscountAmount,
          // Entered coupon merges server-side so the Payment dialog total matches the sale.
          ...(couponCode ? { couponCode } : {}),
        }
      : null;

  // The preview is the single owner of the auto-applied discount shown pre-payment,
  // and it must be promotion-aware for ANY user who can ring a sale — the promotion
  // applies to their sale regardless of whether they can manage/read promotions.
  // `/promotions/preview` is guarded by `pos:create` (the sale-maker permission), so
  // cashiers can call it. It is intentionally NOT gated on `canReadPromotions` or on
  // the `activePromotions` badge list (which needs promotions:read); the preview
  // endpoint itself runs the promotion engine and returns [] when nothing applies. (T-356)
  // Session id changes whenever the cart empties, so a PRIOR cart's discount is never
  // carried onto the next cart's first fetch (T-375); edits within one cart keep it steady.
  const session = usePreviewSession(!!cartPayload);
  const {
    data: fetchedPreview,
    isError: previewFailed,
    isFetching,
  } = useQuery({
    queryKey: ['promotion-preview', session, cartPayload],
    queryFn: () =>
      api.post('/promotions/preview', cartPayload).then((r) => r.data as PromotionPreviewLine[]),
    enabled: !!cartPayload && isOnline,
    staleTime: 0,
    // Keep the last good discount visible while a new cart edit re-fetches, so the
    // Total never flashes back to the pre-discount amount between keystrokes. (T-369)
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[1] === session ? previousData : undefined,
  });

  // POS-I78: with no connection the same shared engine runs on the promotions cached from the
  // server, so the offline cart, Payment dialog and receipt show the promotion the server will apply.
  const offlineData = useOfflinePricingData(!isOnline && !!cartPayload);
  const orderDiscountForOffline = useCartStore((s) => s.orderDiscountAmount);
  const offlinePreview: PromotionPreviewLine[] = (() => {
    if (isOnline || !cartPayload) return EMPTY_PREVIEW;
    const { applied } = previewOfflinePromotions(items, orderDiscountForOffline, offlineData.promotions);
    return applied.map((a) => ({
      promotionId: a.promotionId,
      promotionName: a.promotionName,
      discountAmount: a.discountAmount,
    }));
  })();

  // No cart => no discount, regardless of any cached/placeholder value. (T-375)
  const promotionPreview = !cartPayload
    ? EMPTY_PREVIEW
    : isOnline
      ? fetchedPreview ?? EMPTY_PREVIEW
      : offlinePreview;
  // True only on a cart's first fetch (nothing to show yet) — consumers may show a cue.
  const promotionPreviewPending = !!cartPayload && isOnline && isFetching && fetchedPreview === undefined;

  const promotionDiscount = promotionPreview.reduce((sum, p) => sum + p.discountAmount, 0);

  // When the preview call ERRORS we must not silently show the pre-discount total —
  // consumers surface `previewFailed` so a failed preview is visible rather than
  // invisibly reintroducing the overstated-charge divergence T-356 fixed. (T-369)
  return {
    activePromotions,
    promotionPreview,
    promotionDiscount,
    previewFailed: isOnline ? previewFailed : false,
    promotionPreviewPending,
  };
}
